import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// Detalle de empresas canceladas (destino de la tarjeta "Empresas canceladas" del panel de
// super admin). Separado del panel central a propósito: ahí solo viven las empresas operando
// hoy (activa, periodo de gracia, solo lectura, sin suscripción). Desde aquí se puede
// reactivar una empresa sin pasar por Stripe (igual que el resto de los cambios manuales de
// estado de facturación en este panel).
export default function CanceledCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(null);
  const [reactivating, setReactivating] = useState(null);

  const load = () => {
    api.get("/superadmin/companies?billing_status=canceled").then(({ data }) => setCompanies(data));
  };

  useEffect(load, []);

  const reactivate = async (tenantId, companyName) => {
    if (!window.confirm(`¿Reactivar a ${companyName}? Su facturación volverá a estado "Activa".`)) return;
    setReactivating(tenantId);
    try {
      await api.post(`/superadmin/companies/${tenantId}/reactivate`);
      toast.success(`${companyName} reactivada`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReactivating(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-petroleum text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold">CERH · Super Admin</span>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => navigate("/superadmin")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al panel
        </Button>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="font-heading text-xl text-charcoal mb-1">Empresas canceladas</h1>
        <p className="text-sm text-charcoal/60 mb-6">No suman al ingreso mensual mientras estén canceladas.</p>

        <div className="space-y-3">
          {companies?.map((c) => (
            <div key={c.id} className="bg-card border border-petroleum/10 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-charcoal">{c.company_name}</p>
                <p className="text-xs text-charcoal/50 mt-0.5">{(Array.isArray(c.admin_emails) ? c.admin_emails.join(", ") : "") || "sin admin"}</p>
                <p className="text-xs text-charcoal/50 mt-0.5">
                  {c.employee_count ?? "—"} empleado(s) · última banda: {c.plan_tier || "—"}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                className="rounded-full h-8 text-xs bg-petroleum hover:bg-petroleum/90"
                disabled={reactivating === c.id}
                onClick={() => reactivate(c.id, c.company_name)}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivar
              </Button>
            </div>
          ))}
          {companies?.length === 0 && <p className="text-sm text-charcoal/50">No hay empresas canceladas.</p>}
          {companies === null && <p className="text-sm text-charcoal/50">Cargando…</p>}
        </div>
      </div>
    </div>
  );
}
